import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const HERO_IMAGE =
  'https://roadtechservices.netlify.app/images/mower-repair-3-850w.jpeg';
const SIDE_IMAGE =
  'https://roadtechservices.netlify.app/images/generator-repair-1-850w.jpeg';

const whoThisIsFor = [
  {
    icon: 'ri-tools-line',
    title: 'DIY Homeowners',
    description:
      "You're comfortable doing your own repairs but you're stuck on a diagnosis. Matt can walk you through what to look for and what the likely fix is.",
  },
  {
    icon: 'ri-map-pin-off-line',
    title: "Outside Our Service Area",
    description:
      "We can't always make it out in person — but that doesn't mean you're on your own. A live call gets you the same expertise, wherever you are.",
  },
  {
    icon: 'ri-question-line',
    title: 'Unsure Where to Start',
    description:
      "Equipment acting up but you don't know if it's worth fixing? Get a professional read on the situation before you invest any money.",
  },
  {
    icon: 'ri-time-line',
    title: 'Need Help Fast',
    description:
      "Don't want to wait on a shop or a service call? Book a same-day consultation and get answers quickly.",
  },
];

const whatMattCanHelp = [
  'Lawn mower starting, engine, and cutting issues',
  'Snow blower auger, belt, and starting problems',
  'Generator engine, fuel, and electrical faults',
  'Briggs & Stratton, Kohler, and Kawasaki engine diagnosis',
  'Stihl equipment troubleshooting',
  'Pre-season inspection advice',
  'Post-repair verification & testing guidance',
  'Parts identification & sourcing advice',
];

const howItWorks = [
  {
    step: '1',
    title: 'Book Your Slot',
    description:
      'Choose a time that works for you. Consultations can be scheduled in advance or arranged same-day when available.',
  },
  {
    step: '2',
    title: 'Prepare Your Equipment',
    description:
      'Have your equipment accessible and ready. Note the symptoms, error codes, or anything unusual you\'ve observed.',
  },
  {
    step: '3',
    title: 'Connect with Matt',
    description:
      'Join via phone, Zoom, or Google Meet. Matt will ask targeted questions, walk through the diagnosis process with you, and help identify the issue.',
  },
  {
    step: '4',
    title: 'Get Your Answer',
    description:
      'Leave the call with a clear understanding of what\'s wrong, what needs to be done, and whether you can fix it yourself or need a professional.',
  },
];

const tiers = [
  {
    name: '15-Minute Call',
    duration: '15 min',
    icon: 'ri-time-line',
    description:
      'Quick targeted help for a specific issue. Best for single-symptom diagnosis or a focused technical question.',
    includes: [
      'Live 1-on-1 with Matt',
      'Phone, Zoom, or Google Meet',
      'Focused diagnosis for one issue',
      'Actionable next steps',
    ],
    cta: 'Book 15-Min Call',
    highlighted: false,
  },
  {
    name: '30-Minute Call',
    duration: '30 min',
    icon: 'ri-customer-service-2-line',
    description:
      'More time for complex issues, multiple symptoms, or when you need Matt to walk through a full diagnostic process with you step by step.',
    includes: [
      'Live 1-on-1 with Matt',
      'Phone, Zoom, or Google Meet',
      'In-depth diagnostic walkthrough',
      'Multiple issues covered',
      'Repair guidance & parts advice',
    ],
    cta: 'Book 30-Min Call',
    highlighted: true,
  },
];

const faqs = [
  {
    q: 'What equipment can Matt help diagnose on a call?',
    a: 'Lawn mowers (push & riding), snow blowers (single & two-stage), and generators of all major brands — including Briggs & Stratton, Kohler, Kawasaki, and Stihl.',
  },
  {
    q: 'How do I join the call?',
    a: 'You can connect via phone call, Zoom, or Google Meet — whichever works best for you. Video is helpful when Matt needs to visually assess the equipment, but phone works for most issues.',
  },
  {
    q: 'Can I schedule in advance?',
    a: 'Yes. You can schedule your consultation in advance to lock in a specific date and time. Same-day slots may also be available depending on availability.',
  },
  {
    q: 'Is this useful if I\'m not local to Michigan?',
    a: "Absolutely. The consultation is available nationwide. Matt's expertise and certifications apply whether you're in Michigan or anywhere else in the country.",
  },
  {
    q: 'What if my issue isn\'t resolved in the call?',
    a: "Matt will give you honest guidance. If the issue requires hands-on service and you're in the Michigan area, he may be able to schedule an in-person visit.",
  },
  {
    q: 'How is this different from just searching online?',
    a: "Generic search results won't account for your specific equipment, symptoms, or situation. Matt can ask follow-up questions, narrow down the diagnosis in real time, and give you tailored guidance — not generic advice.",
  },
];

export default function Consultation() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="font-body">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="Road Tech Services expert consultation"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/88 via-black/70 to-black/40" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full pt-20">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-orange-600/20 border border-orange-500/40 text-orange-400 text-xs font-semibold uppercase tracking-widest px-4 py-2 rounded-full mb-7">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-line text-sm" />
              </div>
              Live Expert Troubleshooting
            </div>

            <h1 className="font-display text-5xl lg:text-7xl font-extrabold text-white uppercase tracking-tight leading-none mb-3">
              Stuck on a
            </h1>
            <h1 className="font-display text-5xl lg:text-7xl font-extrabold text-orange-500 uppercase tracking-tight leading-none mb-6">
              Diagnosis?
            </h1>

            <p className="text-white/80 text-lg leading-relaxed mb-4 max-w-xl">
              Book a live 1-on-1 troubleshooting call with Matt — a Briggs
              &amp; Stratton Master Certified Technician with years of hands-on
              small engine repair experience.
            </p>
            <p className="text-white/60 text-base leading-relaxed mb-10 max-w-xl">
              Get expert guidance for lawn mowers, snow blowers, and generators
              — by phone, Zoom, or Google Meet. Available locally and
              nationwide.
            </p>

            <div className="flex flex-wrap gap-4">
              <a
                href="#book"
                className="bg-orange-600 hover:bg-orange-700 text-white font-semibold px-8 py-4 rounded-lg transition-colors whitespace-nowrap text-sm cursor-pointer"
              >
                Book a Consultation
              </a>
              <a
                href="#how-it-works"
                className="bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold px-8 py-4 rounded-lg transition-colors whitespace-nowrap text-sm backdrop-blur-sm cursor-pointer"
              >
                How It Works
              </a>
            </div>

            <div className="mt-10 flex flex-wrap gap-6">
              {[
                { icon: 'ri-phone-line', text: 'Phone, Zoom, or Google Meet' },
                { icon: 'ri-map-pin-2-line', text: 'Available Nationwide' },
                { icon: 'ri-shield-star-line', text: 'Master Certified Technician' },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-2 text-white/60 text-xs">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${item.icon} text-orange-400 text-base`} />
                  </div>
                  {item.text}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="bg-white py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <p className="text-orange-600 text-xs font-bold uppercase tracking-widest mb-3">
              Is This for You?
            </p>
            <h2 className="font-display text-4xl lg:text-5xl font-bold uppercase tracking-tight text-neutral-900">
              Who This Is For
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {whoThisIsFor.map((item) => (
              <div
                key={item.title}
                className="bg-stone-50 rounded-xl p-6 border border-stone-100 hover:border-orange-200 transition-colors"
              >
                <div className="w-11 h-11 flex items-center justify-center bg-orange-100 rounded-xl mb-4">
                  <i className={`${item.icon} text-orange-600 text-lg`} />
                </div>
                <h3 className="font-semibold text-neutral-900 text-sm mb-2">
                  {item.title}
                </h3>
                <p className="text-neutral-500 text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Matt Can Help With */}
      <section className="bg-neutral-900 py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-orange-500 text-xs font-bold uppercase tracking-widest mb-4">
                Areas of Expertise
              </p>
              <h2 className="font-display text-4xl lg:text-5xl font-bold uppercase tracking-tight text-white mb-6 leading-tight">
                What Matt Can Help You With
              </h2>
              <p className="text-neutral-400 text-base leading-relaxed mb-10">
                With certifications across major small engine brands and years
                of hands-on field experience, Matt can cover a wide range of
                equipment issues — from simple fixes to complex diagnostics.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {whatMattCanHelp.map((item) => (
                  <div
                    key={item}
                    className="flex items-start gap-3 text-sm text-neutral-300"
                  >
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-fill text-orange-500 text-base" />
                    </div>
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div className="h-[500px] rounded-2xl overflow-hidden">
              <img
                src={SIDE_IMAGE}
                alt="Matt Olenzek expert technician consultation"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="bg-white py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <p className="text-orange-600 text-xs font-bold uppercase tracking-widest mb-3">
              Simple Process
            </p>
            <h2 className="font-display text-4xl lg:text-5xl font-bold uppercase tracking-tight text-neutral-900">
              How the Call Works
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {howItWorks.map((step) => (
              <div key={step.step} className="relative">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-orange-600 text-white font-display text-xl font-bold flex items-center justify-center flex-shrink-0">
                    {step.step}
                  </div>
                  <div className="flex-1 h-px bg-stone-200 last:hidden" />
                </div>
                <h3 className="font-semibold text-neutral-900 text-base mb-2">
                  {step.title}
                </h3>
                <p className="text-neutral-500 text-sm leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Tiers */}
      <section id="book" className="bg-stone-50 py-24 lg:py-32">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <p className="text-orange-600 text-xs font-bold uppercase tracking-widest mb-3">
              Consultation Options
            </p>
            <h2 className="font-display text-4xl lg:text-5xl font-bold uppercase tracking-tight text-neutral-900 mb-4">
              Choose Your Call Length
            </h2>
            <p className="text-neutral-500 text-base max-w-xl mx-auto leading-relaxed">
              Select the option that fits your situation. Both give you direct
              access to Matt and his expertise.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {tiers.map((tier) => (
              <div
                key={tier.name}
                className={`rounded-2xl p-8 border-2 ${
                  tier.highlighted
                    ? 'bg-neutral-900 border-orange-600'
                    : 'bg-white border-stone-200'
                }`}
              >
                {tier.highlighted && (
                  <div className="inline-block bg-orange-600 text-white text-xs font-semibold px-3 py-1 rounded-full mb-4">
                    Most Popular
                  </div>
                )}
                <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-xl mb-5">
                  <i className={`${tier.icon} text-orange-600 text-xl`} />
                </div>
                <h3
                  className={`font-display text-2xl font-bold uppercase tracking-tight mb-1 ${
                    tier.highlighted ? 'text-white' : 'text-neutral-900'
                  }`}
                >
                  {tier.name}
                </h3>
                <div
                  className={`text-sm mb-4 ${
                    tier.highlighted ? 'text-neutral-400' : 'text-neutral-500'
                  }`}
                >
                  {tier.duration} live call with Matt
                </div>
                <p
                  className={`text-sm leading-relaxed mb-6 ${
                    tier.highlighted ? 'text-neutral-300' : 'text-neutral-600'
                  }`}
                >
                  {tier.description}
                </p>

                <ul className="space-y-2.5 mb-8">
                  {tier.includes.map((item) => (
                    <li key={item} className="flex items-center gap-3 text-sm">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className="ri-checkbox-circle-fill text-orange-500 text-base" />
                      </div>
                      <span
                        className={
                          tier.highlighted ? 'text-neutral-300' : 'text-neutral-600'
                        }
                      >
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>

                <a
                  href="tel:248-781-0600"
                  className={`block w-full text-center font-semibold px-6 py-3.5 rounded-lg transition-colors text-sm cursor-pointer whitespace-nowrap ${
                    tier.highlighted
                      ? 'bg-orange-600 hover:bg-orange-700 text-white'
                      : 'bg-neutral-900 hover:bg-neutral-800 text-white'
                  }`}
                >
                  {tier.cta}
                </a>
              </div>
            ))}
          </div>

          <p className="text-center text-neutral-400 text-xs mt-6">
            To book, call{' '}
            <a href="tel:248-781-0600" className="text-orange-600 hover:text-orange-700 font-semibold cursor-pointer">
              (248) 781-0600
            </a>{' '}
            or email{' '}
            <a href="mailto:Road.tech7@gmail.com" className="text-orange-600 hover:text-orange-700 font-semibold cursor-pointer">
              Road.tech7@gmail.com
            </a>
          </p>
        </div>
      </section>

      {/* Why This vs Guessing */}
      <section className="bg-white py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <p className="text-orange-600 text-xs font-bold uppercase tracking-widest mb-3">
              The Smarter Move
            </p>
            <h2 className="font-display text-4xl lg:text-5xl font-bold uppercase tracking-tight text-neutral-900">
              Why This Beats Guessing
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {[
              {
                icon: 'ri-search-eye-line',
                title: 'vs. Random Online Searches',
                description:
                  'Generic forum answers and YouTube videos can\'t account for your specific equipment, its exact symptoms, or how it was maintained. Matt can.',
              },
              {
                icon: 'ri-money-dollar-circle-line',
                title: 'vs. Throwing Parts at It',
                description:
                  "Ordering random parts hoping one fixes the issue wastes money fast. A consultation helps you identify the actual problem before spending anything.",
              },
              {
                icon: 'ri-time-line',
                title: 'vs. Waiting on a Shop',
                description:
                  'Equipment repair shops often have weeks-long backlogs. A call with Matt can get you answers today — and possibly back in action this weekend.',
              },
            ].map((item) => (
              <div
                key={item.title}
                className="bg-stone-50 rounded-xl p-7 border border-stone-100"
              >
                <div className="w-11 h-11 flex items-center justify-center bg-orange-100 rounded-xl mb-5">
                  <i className={`${item.icon} text-orange-600 text-lg`} />
                </div>
                <h3 className="font-semibold text-neutral-900 text-base mb-3">
                  {item.title}
                </h3>
                <p className="text-neutral-500 text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-stone-50 py-20 lg:py-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-10">
            <p className="text-orange-600 text-xs font-bold uppercase tracking-widest mb-3">
              FAQs
            </p>
            <h2 className="font-display text-3xl lg:text-4xl font-bold uppercase tracking-tight text-neutral-900">
              Consultation FAQs
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="bg-white border border-stone-200 rounded-xl overflow-hidden"
              >
                <button
                  className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-stone-50 transition-colors"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="font-semibold text-neutral-900 text-sm pr-4">
                    {faq.q}
                  </span>
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i
                      className={`${
                        openFaq === i ? 'ri-subtract-line' : 'ri-add-line'
                      } text-orange-600 text-lg`}
                    />
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5">
                    <p className="text-neutral-600 text-sm leading-relaxed">
                      {faq.a}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-neutral-900 py-24 lg:py-32">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-orange-500 text-xs font-bold uppercase tracking-widest mb-4">
            Get Expert Troubleshooting Help
          </p>
          <h2 className="font-display text-4xl lg:text-6xl font-bold uppercase tracking-tight text-white mb-5 leading-tight">
            Stop Guessing. Book Your Call.
          </h2>
          <p className="text-neutral-400 text-base leading-relaxed mb-10 max-w-xl mx-auto">
            A quick call with Matt can save you hours of frustration, wasted
            parts, and unnecessary expense. Get the answer you need from
            someone who actually knows small engine repair.
          </p>

          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <a
              href="tel:248-781-0600"
              className="bg-orange-600 hover:bg-orange-700 text-white font-semibold px-8 py-4 rounded-lg transition-colors whitespace-nowrap text-sm cursor-pointer"
            >
              Schedule Your Call
            </a>
            <Link
              to="/contact"
              className="bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold px-8 py-4 rounded-lg transition-colors whitespace-nowrap text-sm cursor-pointer"
            >
              Send a Message
            </Link>
          </div>

          <div className="flex flex-wrap justify-center gap-8 text-neutral-500 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-line text-orange-500" />
              </div>
              (248) 781-0600
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-mail-line text-orange-500" />
              </div>
              Road.tech7@gmail.com
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-map-pin-line text-orange-500" />
              </div>
              Available Nationwide
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
